import { create } from 'zustand'

export type NotificationType = 'jackpot_win' | 'room_invite' | 'transaction' | 'system' | 'game'

export interface Notification {
  id: string
  type: NotificationType
  title: string
  message: string
  timestamp: Date
  read: boolean
}

interface NotificationState {
  notifications: Notification[]
  history: Notification[]
  unreadCount: number
  markAsRead: (id: string) => void
  markAllAsRead: () => void
  dismiss: (id: string) => void
  clearAll: () => void
}

function countUnread(notifications: Notification[]): number {
  return notifications.filter((n) => !n.read).length
}

const HOUR = 1000 * 60 * 60
const DAY = HOUR * 24

const mockNotifications: Notification[] = [
  { id: '1', type: 'jackpot_win', title: '累積獎金中獎！', message: '恭喜！您在皇家同花順中贏得了 $17,777 累積獎金！', timestamp: new Date(Date.now() - 1000 * 60 * 2), read: false },
  { id: '2', type: 'room_invite', title: '房間邀請', message: 'David 邀請您加入「高額桌 VIP」房間', timestamp: new Date(Date.now() - 1000 * 60 * 15), read: false },
  { id: '3', type: 'transaction', title: '存款成功', message: '您的 $100.00 存款已成功到帳', timestamp: new Date(Date.now() - 1000 * 60 * 45), read: false },
  { id: '4', type: 'game', title: '牌局結算', message: '您在「新手桌 #3」贏得 $45.20', timestamp: new Date(Date.now() - HOUR), read: false },
  { id: '5', type: 'system', title: '系統維護通知', message: '系統將於今晚 03:00 進行例行維護，預計 30 分鐘', timestamp: new Date(Date.now() - HOUR * 3), read: true },
  { id: '6', type: 'jackpot_win', title: '累積獎金池更新', message: '累積獎金池已突破 $88,888！', timestamp: new Date(Date.now() - HOUR * 6), read: true },
  { id: '7', type: 'transaction', title: '提款處理中', message: '您的 $50.00 提款申請已提交，預計 24 小時內到帳', timestamp: new Date(Date.now() - DAY), read: true },
  { id: '8', type: 'room_invite', title: '房間邀請', message: 'Sarah 邀請您加入「週末錦標賽」房間', timestamp: new Date(Date.now() - DAY * 2), read: true },
]

const olderHistory: Notification[] = [
  { id: '9', type: 'game', title: '牌局結算', message: '您在「中級桌 #7」輸了 $22.00', timestamp: new Date(Date.now() - DAY * 2 - HOUR * 5), read: true },
  { id: '10', type: 'transaction', title: '存款成功', message: '您的 $250.00 存款已成功到帳', timestamp: new Date(Date.now() - DAY * 3), read: true },
  { id: '11', type: 'system', title: '帳戶安全提醒', message: '偵測到新裝置登入，如非本人操作請立即更改密碼', timestamp: new Date(Date.now() - DAY * 3 - HOUR * 8), read: true },
  { id: '12', type: 'game', title: '牌局結算', message: '您在「高額桌 VIP」贏得 $320.50', timestamp: new Date(Date.now() - DAY * 4), read: true },
  { id: '13', type: 'jackpot_win', title: '累積獎金池更新', message: '累積獎金池已突破 $50,000！', timestamp: new Date(Date.now() - DAY * 5), read: true },
  { id: '14', type: 'room_invite', title: '房間邀請', message: 'Mike 邀請您加入「週三常規桌」房間', timestamp: new Date(Date.now() - DAY * 5 - HOUR * 3), read: true },
  { id: '15', type: 'transaction', title: '提款完成', message: '您的 $200.00 提款已成功到帳', timestamp: new Date(Date.now() - DAY * 6), read: true },
  { id: '16', type: 'game', title: '牌局結算', message: '您在「新手桌 #1」贏得 $15.80', timestamp: new Date(Date.now() - DAY * 7), read: true },
  { id: '17', type: 'system', title: '功能更新', message: '新增累積獎金系統！符合資格的手牌可贏取額外獎金', timestamp: new Date(Date.now() - DAY * 8), read: true },
  { id: '18', type: 'transaction', title: '存款成功', message: '您的 $500.00 首次存款已到帳，歡迎加入 PWG Lounge！', timestamp: new Date(Date.now() - DAY * 10), read: true },
]

const allHistory = [...mockNotifications, ...olderHistory]

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: mockNotifications,
  history: allHistory,
  unreadCount: countUnread(mockNotifications),

  markAsRead: (id) =>
    set((s) => {
      const notifications = s.notifications.map((n) => n.id === id ? { ...n, read: true } : n)
      const history = s.history.map((n) => n.id === id ? { ...n, read: true } : n)
      return { notifications, history, unreadCount: countUnread(notifications) }
    }),

  markAllAsRead: () =>
    set((s) => ({
      notifications: s.notifications.map((n) => ({ ...n, read: true })),
      history: s.history.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    })),

  dismiss: (id) =>
    set((s) => {
      const notifications = s.notifications.filter((n) => n.id !== id)
      return { notifications, unreadCount: countUnread(notifications) }
    }),

  clearAll: () =>
    set((s) => ({
      notifications: [],
      history: s.history.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    })),
}))
